import React, { Fragment, Component } from 'react';
import { Link, Redirect } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as assessmentsActions from '../../redux/actions/assessmentsActions';
import * as sectionsActions from '../../redux/actions/sectionsActions';
import { bodyScroll } from '../../utils/utils';
import { parseUnderlines } from '../../utils/parseUnderlines';

import QuestionHead from '../features/assessment/QuestionHead';
import QuestionPagination from '../features/assessment/QuestionPagination';
import QuestionSwitch from '../features/assessment/QuestionSwitch';
import StickyButtons from '../features/assessment/StickyButtons';
import MedicationListModal from '../features/assessment/MedicationListModal';
import getUnansweredQuestions from '../../utils/unansweredQuestions';
import SimultaneousEditingModal from '../features/assessment/SimultaneousEditingModal';

class QuestionPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showMedicationListModal: false,
      showSimultaneousEditingModal: false,
    };
  }

  componentDidMount() {
    const { match, assessments, currentAssessment, dispatchAssessmentsActions } = this.props;

    if (!assessments[match.params.assessmentId] || !Object.keys(assessments[match.params.assessmentId].answers).length) {
      // We must have the deep version of the assessment
      dispatchAssessmentsActions.getAssessment(match.params.assessmentId, true, this.onGetAssessmentFail);
    } else if (currentAssessment.id !== match.params.assessmentId) {
      // No need for more from the database, just update the current assessment
      dispatchAssessmentsActions.updateCurrentAssessment(assessments[match.params.assessmentId]);
    }
  }

  componentDidUpdate(prevProps) {
    const { location } = this.props;
    // When we change pages, force refocus to the first input
    if (location !== prevProps.location) {
      document.querySelector('.new-assessment-detail__form input, .new-assessment-detail__form textarea').focus();
    }
  }

  componentWillUnmount() {
    const { dispatchAssessmentsActions } = this.props;
    // Reset the current assessment if we navigate away
    dispatchAssessmentsActions.clearCurrentAssessment();
  }

  toggleMedicationListModal = () => {
    bodyScroll.toggle();
    this.setState(prevState => ({ showMedicationListModal: !prevState.showMedicationListModal }));
  }

  toggleSimultaneousEditingModal = () => {
    bodyScroll.toggle();
    this.setState(prevState => ({ showSimultaneousEditingModal: !prevState.showSimultaneousEditingModal }));
  }

  onGetAssessmentFail = () => {
    const { history } = this.props;
    history.push('/assessments');
  }

  handleAnswerChange = (parts) => {
    const { dispatchAssessmentsActions } = this.props;

    dispatchAssessmentsActions.saveAnswer({
      id: this.currentQuestion.id,
      index: this.currentQuestion.index,
      answer: { parts },
    });
  }

  handleNotesChange = (e, key) => {
    const { dispatchAssessmentsActions } = this.props;

    dispatchAssessmentsActions.saveNotes({
      id: this.currentQuestion.id,
      index: this.currentQuestion.index,
      notes: { [key]: e.target.value },
    });
  }

  putAnswer = () => {
    const { dispatchAssessmentsActions, currentAssessment } = this.props;
    dispatchAssessmentsActions.putAssessment(currentAssessment, false, this.onPutAssessmentFail);
  }

  onPutAssessmentFail = (err) => {
    if (err.response && err.response.status === 409) {
      bodyScroll.toggle();
      this.setState({ showSimultaneousEditingModal: true });
    }
  }

  toggleFlag = () => {
    const { dispatchAssessmentsActions } = this.props;
    dispatchAssessmentsActions.toggleFlag(this.currentQuestion.id, this.currentQuestion.index);
  }

  render() {
    const { match, assessments, currentAssessment, sections, dispatchSectionsActions, dispatchAssessmentsActions, history, status, users } = this.props;
    const { showMedicationListModal, showSimultaneousEditingModal } = this.state;

    if (match.params.assessmentId && !assessments[match.params.assessmentId]) {
      // We don't have the selected assessment yet
      return null;
    }
    if (!currentAssessment.section) {
      // We don't have the current assessment yet (or it's corrupted)
      return null;
    }
    if (!sections || !sections[currentAssessment.section] || !sections[currentAssessment.section].questions) {
      // We don't have any sections or we don't have the current section or we don't have the deep version of the current section
      dispatchSectionsActions.getSection(currentAssessment.section);
      return null;
    }
    this.currentSection = sections[currentAssessment.section];
    this.currentQuestion = Object.values(this.currentSection.questions).find(question => (
      question.index === parseInt(match.params.questionIndex, 10) - 1 // human-readable index starts at 1 in the url
    ));
    if (!this.currentQuestion) {
      return <Redirect to={`/assessments/${currentAssessment.id}/final-comments`} />;
    }
    const currentAnswers = currentAssessment.answers[this.currentQuestion.id] || {
      flagged: false,
      parts: {},
    };
    const unansweredQuestions = getUnansweredQuestions(this.currentSection.questions, currentAssessment.answers);
    const nextUnansweredQuestion = unansweredQuestions.filter(question => question.index > this.currentQuestion.index)[0];
    return currentAssessment.dateCompleted
      ? (
        <Redirect to={`/assessments/${currentAssessment.id}/complete`} />
      ) : (
        <Fragment>
          <Helmet>
            <title>New Assessment</title>
          </Helmet>
          <StickyButtons
            toggleFlag={this.toggleFlag}
            flagged={currentAnswers.flagged}
          />
          <div className="new-assessment-detail">
            <div className="row">
              <div className={'new-assessment-detail__heading color--' + this.currentSection.theme}>
                {sections[currentAssessment.section].fullTitle}
              </div>
              {this.currentQuestion.instructions && (
                <div className={'new-assessment-detail__instructions new-assessment-detail__instructions--' + this.currentSection.theme}>
                  {parseUnderlines(this.currentQuestion.instructions)}
                </div>
              )}
              {this.currentQuestion.jumpText && this.currentQuestion.jumpIndex && (
                <div className="new-assessment-detail__jump-link">
                  <Link
                    className="flat-link"
                    to={`/assessments/${currentAssessment.id}/${this.currentQuestion.jumpIndex}`}
                  >
                    {this.currentQuestion.jumpText}
                  </Link>
                </div>
              )}
            </div>
            <div className="row">
              <div className="new-assessment-detail__content">
                <QuestionHead
                  question={this.currentQuestion}
                  toggleMedicationListModal={this.toggleMedicationListModal}
                  currentSection={this.currentSection}
                />
                <div className="form new-assessment-detail__form">
                  {this.currentQuestion.questionType === 'multipart'
                    ? (
                      <QuestionSwitch
                        question={this.currentQuestion}
                        answer={currentAnswers}
                        handleAnswerChange={this.handleAnswerChange}
                        saveAnswer={dispatchAssessmentsActions.saveAnswer}
                      />
                    ) : (
                      <div className="new-assessment-detail__part">
                        <QuestionSwitch
                          question={this.currentQuestion}
                          answer={currentAnswers}
                          handleAnswerChange={this.handleAnswerChange}
                          saveAnswer={dispatchAssessmentsActions.saveAnswer}
                        />
                      </div>
                    )
                  }
                  <div className="new-assessment-detail__part new-assessment-detail__part--margin-top">
                    <label htmlFor="observations" className="form__text-label">
                      Observations and Responses:
                    </label>
                    <textarea
                      id="observations"
                      name="observations"
                      onChange={e => this.handleNotesChange(e, 'observations')}
                      value={currentAnswers.observations || ''}
                    />
                  </div>
                  <div className="new-assessment-detail__part">
                    <label htmlFor="opportunities" className="form__text-label">
                      Opportunities for Improvement and Consultative Recommendations:
                    </label>
                    <textarea
                      id="opportunities"
                      name="opportunities"
                      onChange={e => this.handleNotesChange(e, 'opportunities')}
                      value={currentAnswers.opportunities || ''}
                    />
                  </div>
                </div>
              </div>
              <QuestionPagination
                putAssessment={this.putAnswer}
                navigate={history.push}
                currentQuestionIndex={parseInt(match.params.questionIndex, 10)}
                currentSection={this.currentSection}
              />
              {status.revisitingAssessment && status.revisitingAssessment === currentAssessment.id && nextUnansweredQuestion && (
                <div className="new-assessment-detail__pagination-wrapper new-assessment-detail__pagination-wrapper--link">
                  <Link to={`/assessments/${currentAssessment.id}/${nextUnansweredQuestion.index + 1}`} className="flat-link">
                    Advance to Next Unanswered Question
                  </Link>
                </div>
              )}
            </div>
          </div>
          {this.currentQuestion.hasMedicationList && (
            <MedicationListModal
              visible={showMedicationListModal}
              toggleModal={this.toggleMedicationListModal}
              list={this.currentQuestion.medicationList}
            />
          )}
          <SimultaneousEditingModal
            visible={showSimultaneousEditingModal}
            toggleModal={this.toggleSimultaneousEditingModal}
            currentAssessment={currentAssessment}
            lastEditedBy={users[currentAssessment.lastEditedBy]}
            forcePutAssessment={dispatchAssessmentsActions.forcePutAssessment}
            splitPutAssessment={dispatchAssessmentsActions.splitPutAssessment}
            history={history}
            currentQuestionIndex={parseInt(match.params.questionIndex, 10)}
          />
        </Fragment>
      );
  }
}

function mapStateToProps(state) {
  return {
    sections: state.sections,
    currentAssessment: state.assessments.currentAssessment,
    assessments: state.assessments.list,
    status: state.status,
    users: state.users.roster,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    dispatchAssessmentsActions: bindActionCreators(assessmentsActions, dispatch),
    dispatchSectionsActions: bindActionCreators(sectionsActions, dispatch),
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(QuestionPage);



// WEBPACK FOOTER //
// ./src/components/pages/QuestionPage.js